import React, { ReactElement } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { UserState } from "redux-oidc";
import { Link, useHistory } from "react-router-dom";
// style
import styles from "./profiledialog.module.scss";
// store
import IAppState from "infrastructure/store/IAppState";
// components
import Avatar from "features/shared/components/avatar/Avatar";
import CustomerState from "infrastructure/store/customer/CustomerState";
import CustomerSimplifiedDTO from "features/customer/dtos/CustomerSimplifiedDTO";
import ProfileCustomer from "./ProfileCustomer";
import LinkButton from "features/shared/components/buttons/LinkButton";
import { AdminUserAction, setAdminUser } from "infrastructure/store/adminUser/AdminUserActions";
import { Dispatch } from "redux";
import RolesChecker from "features/customer/components/RolesChecker";
import axios from "axios";
import { getChangePasswordUrl } from "features/shared/helpers/urlHelper";
import { useTranslation } from "react-i18next";
import { isAdminUser } from "infrastructure/oidc/oidcHelper";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IPersistentLog from "infrastructure/logging/IPersistentLog";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import ICustomerService from "features/customer/services/ICustomerService";
import PromiseLoadingSpinner from "features/shared/components/loaders/PromiseLoadingSpinner";
import { Shared_ProfileDialog } from "features/shared/components/loaders/promiseLoadingAreas";

interface IProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  customers: CustomerSimplifiedDTO[];
  customersCount: number;
}

const ProfileDialog: React.FC<IProps> = (props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;

  // DI
  const persistentLog = useInjection<IPersistentLog>(IocTypes.PersistentLog);
  const customerService = useInjection<ICustomerService>(IocTypes.CustomerService);

  // router
  const history = useHistory();

  // store
  const adminUserDispatch = useDispatch<Dispatch<AdminUserAction>>();
  const userState = useSelector<IAppState, UserState>((store) => store.oidc);
  const customerState = useSelector<IAppState, CustomerState>((store) => store.customer);

  // props/vars
  const { show, setShow, customers, customersCount } = props;
  const user = userState.user;
  const actualCustomer = customerState.customer;

  // handlers
  const closeDialogHandler = () => {
    setShow(false);
  };

  const selectCustomerHandler = async (customer: CustomerSimplifiedDTO) => {
    persistentLog.customerLogin(customer, isAdminUser(user));

    await customerService.loadAndStoreCustomerData(customer, user?.profile.role);

    closeDialogHandler();

    history.push("/");
  };

  const switchToAllCustomerHandler = () => {
    customerService.clearCustomerDataFromStore();
  };

  const switchToUserSelectHandler = () => {
    // set admin email to axios defaults
    axios.defaults.headers.common["email"] = user?.profile.email;
    // clear selected user
    adminUserDispatch(setAdminUser({
      isSelectedUser: false,
      email: null
    }));
    // clear selected customer
    customerService.clearCustomerDataFromStore();
  }
  
  // sub-renders
  const renderCustomers = (
    customers: CustomerSimplifiedDTO[],
    actualCustomer: CustomerSimplifiedDTO
  ): ReactElement[] => {
    let filteredCustomers = customers.filter(
      (x) => x.customerId !== actualCustomer.customerId
    );
    return filteredCustomers.map((customer) => (
      <ProfileCustomer
        customer={customer}
        isActive={false}
        onCustomerClick={selectCustomerHandler}
        key={customer.customerId}
      />
    ));
  };

  const renderAdminActions = () => {
    if (isAdminUser(user)) {
      return (
        <>
          <span className={styles.profileActionItem} role="button" onClick={switchToUserSelectHandler}>
            <span className={styles.profileActionIcon}>
              <FontAwesomeIcon icon="cog" />
            </span>
            <span>{tLabels("profileDialog.administration")}</span>
          </span>
        </>
      );
    }
  }

  const renderCustomerAdminActions = () => {
    return (
      <RolesChecker enabledRoles="Admin">
        <Link 
          to="/accounts" 
          className={styles.profileActionItem} 
          onClick={() => setShow(false)}>
          <span className={styles.profileActionIcon}>
            <FontAwesomeIcon icon="users" />
          </span>
          <span>{tLabels("profileDialog.accountManagement")}</span>
        </Link>
      </RolesChecker>
    );
  }

  // render
  return (
    <Modal
      show={show}
      onHide={closeDialogHandler}
      dialogClassName={styles.profileDialog}
      className={styles.profileModal}
    >
      <div className={`modal-content ${styles.profileContent}`}>
        <div className={styles.profileHeader}>
          <button
            type="button"
            className={`close ${styles.profileCloseBtn}`}
            onClick={closeDialogHandler}
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="row no-gutters align-items-center">
            <div className="col-auto pr-3">
              <Avatar size={60} icon="user" />
            </div>
            <div className="col">
              <small className={styles.profileHeaderInfo}>
                {tLabels("profileDialog.loggedUser")}
              </small>
              <h6 className="mb-0 text-break">{user?.profile.email}</h6>
            </div>
          </div>
        </div>
        <div className={`modal-body ${styles.profileBody}`}>
          <div>
            {renderAdminActions()}
            {renderCustomerAdminActions()}
            <a href={getChangePasswordUrl()} className={styles.profileActionItem}>
              <span className={styles.profileActionIcon}>
                <FontAwesomeIcon icon="lock" />
              </span>
              <span>{tLabels("profileDialog.passwordChange")}</span>
            </a>
            <Link to="/logout" className={styles.profileActionItem}>
              <span className={styles.profileActionIcon}>
                <FontAwesomeIcon icon="sign-out-alt" />
              </span>
              <span>{tLabels("profileDialog.logOut")}</span>
            </Link>
          </div>
          <hr className={styles.profileDivider} />
          <h6 className="text-muted">{tLabels("profileDialog.availableCustomers")}</h6>
          <div className="mt-4">
            <ProfileCustomer
              customer={actualCustomer}
              onCustomerClick={selectCustomerHandler}
              isActive
            />
            {renderCustomers(customers, actualCustomer)}
          </div>
          {customersCount > 4 &&
            <div className="text-center mt-3">
            <LinkButton onClick={switchToAllCustomerHandler} />
          </div>
          }
        </div>
      </div>
      <PromiseLoadingSpinner area={Shared_ProfileDialog} />
    </Modal>
  );
};

export default ProfileDialog;
